/**
 * Two-step connect flow for a rewards provider (C2b).
 *
 * Step 1: paste credentials + pick environment, then "Fetch" probes the
 * provider (POST /integrations/rewards/options/) for customers, accounts, and
 * catalog. Step 2: SELECT the customer/account/reward instead of typing
 * identifiers and utids; currency is derived from the chosen reward/account.
 * Only amount + low-balance threshold are typed.
 *
 * Both steps are generated `useForm`s (Step 2's select options come from the
 * fetched data); local state only orchestrates the wizard + holds the async
 * lookup result. The address connect flow is untouched - this is rewards only.
 */

import { useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'

import { toastApiError } from '@/shared/lib/api-error'
import { Button } from '@/shared/components/ui/button'
import { DialogFooter } from '@/shared/components/ui/dialog'
import { useForm, type FormField } from '@/shared/lib/forms'
import {
  useConnectIntegration,
  useFetchRewardsOptions,
} from '@/admin/hooks/use-integrations'
import type { CatalogEntry, RewardsOptions } from '@/admin/types/integrations'

interface RewardsConnectBodyProps {
  entry: CatalogEntry
  onClose: () => void
}

export function RewardsConnectBody({ entry, onClose }: RewardsConnectBodyProps) {
  const connect = useConnectIntegration()
  const fetchOptions = useFetchRewardsOptions()
  const [step, setStep] = useState<'credentials' | 'configure'>('credentials')
  const [creds, setCreds] = useState({
    platform_id: '',
    auth_token: '',
    environment: 'production',
  })
  const [options, setOptions] = useState<RewardsOptions | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)

  const credentialFields = useMemo<FormField[]>(
    () => [
      {
        type: 'password',
        name: 'platform_id',
        label: 'Platform ID',
        placeholder: 'Paste your Platform ID',
        value: '',
        validationSchema: z.string().trim().min(1, 'Platform ID is required.'),
      },
      {
        type: 'password',
        name: 'auth_token',
        label: 'Auth Token',
        placeholder: 'Paste your Auth Token',
        value: '',
        validationSchema: z.string().trim().min(1, 'Auth Token is required.'),
      },
    ],
    [],
  )

  const { Form: CredentialsForm } = useForm(
    { name: 'rewards-credentials-form', submit: 'footer', fields: credentialFields },
    async (raw) => {
      const values = raw as Record<string, string>
      setServerError(null)
      try {
        const opts = await fetchOptions.mutateAsync({
          platform_id: values.platform_id,
          auth_token: values.auth_token,
          environment: 'production',
        })
        if (opts.error) {
          setServerError(opts.error)
          return
        }
        setCreds({
          platform_id: values.platform_id,
          auth_token: values.auth_token,
          environment: 'production',
        })
        setOptions(opts)
        setStep('configure')
      } catch (err: unknown) {
        setServerError('Could not reach the provider. Check the credentials.')
        toastApiError(err, 'Could not fetch accounts and rewards')
      }
    },
    [step],
  )

  const configureFields = useMemo<FormField[]>(() => {
    if (!options) return []
    return [
      {
        type: 'select',
        name: 'customer_identifier',
        label: 'Customer',
        value: '',
        options: options.customers.map((c) => ({
          label: c.display_name || c.customer_identifier,
          value: c.customer_identifier,
        })),
        validationSchema: z.string().min(1, 'Select a customer.'),
      },
      {
        type: 'select',
        name: 'account_identifier',
        label: 'Account',
        value: '',
        options: options.accounts.map((a) => {
          // Surface the account identifier so near-identical names stay distinct.
          const detail = [
            a.display_name ? a.account_identifier : '',
            a.currency_code,
          ]
            .filter(Boolean)
            .join(', ')
          const name = a.display_name || a.account_identifier
          return {
            label: detail ? `${name} (${detail})` : name,
            value: a.account_identifier,
          }
        }),
        validationSchema: z.string().min(1, 'Select an account.'),
      },
      {
        type: 'select',
        name: 'default_utid',
        label: 'Default Reward',
        value: '',
        options: options.catalog.map((i) => ({
          label: `${i.reward_name || i.utid} (${i.utid})`,
          value: i.utid,
        })),
        validationSchema: z.string().min(1, 'Select a reward.'),
      },
      {
        type: 'text',
        name: 'etid',
        label: 'Email Template ID (optional)',
        placeholder: 'Optional',
        value: '',
      },
      {
        type: 'text',
        name: 'low_balance_threshold',
        label: 'Low Balance Threshold (optional)',
        placeholder: 'Optional',
        value: '',
        validationSchema: z
          .string()
          .trim()
          .refine(
            (v) => v === '' || Number(v) >= 0,
            'Enter a non-negative number.',
          ),
      },
    ]
  }, [options])

  const { Form: ConfigureForm } = useForm(
    { name: 'rewards-configure-form', submit: 'footer', fields: configureFields },
    async (raw) => {
      const values = raw as Record<string, string>
      setServerError(null)
      const config: Record<string, string> = {
        customer_identifier: values.customer_identifier,
        account_identifier: values.account_identifier,
        environment: creds.environment,
        default_utid: values.default_utid,
        currency: deriveCurrency(values, options),
      }
      if (values.etid?.trim()) config.etid = values.etid.trim()
      if (values.low_balance_threshold?.trim()) {
        config.low_balance_threshold = values.low_balance_threshold.trim()
      }
      try {
        await connect.mutateAsync({
          category: entry.category,
          provider: entry.provider,
          source: entry.source,
          credentials: {
            platform_id: creds.platform_id,
            auth_token: creds.auth_token,
          },
          config,
        })
        toast.success(`${entry.display_name} enabled`)
        onClose()
      } catch (err: unknown) {
        setServerError(extractMessage(err))
        toastApiError(err, `Failed to enable ${entry.display_name}`)
      }
    },
    [options, creds],
  )

  return (
    <>
      {serverError && (
        <div className="rounded-[4px] border-[0.5px] border-[var(--status-error-bg)] bg-[var(--status-error-bg)] px-3 py-2 text-[12px] text-[var(--status-error)]">
          {serverError}
        </div>
      )}

      {step === 'credentials' ? (
        <>
          <CredentialsForm className="space-y-5" />
          <p className="text-[11px] text-[var(--ink-3)]">
            Stored encrypted. We&apos;ll fetch your accounts and rewards from the
            provider so you can pick them.
          </p>
          <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="rewards-credentials-form"
              disabled={fetchOptions.isPending}
            >
              {fetchOptions.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
              Fetch accounts &amp; rewards
            </Button>
          </DialogFooter>
        </>
      ) : (
        <>
          <p className="text-[12px] text-[var(--ink-3)]">
            Pick the account and reward to use. Currency is taken from the reward.
          </p>
          <ConfigureForm className="space-y-5" />
          <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep('credentials')}
            >
              Back
            </Button>
            <Button
              type="submit"
              form="rewards-configure-form"
              disabled={connect.isPending}
            >
              {connect.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
              Connect
            </Button>
          </DialogFooter>
        </>
      )}
    </>
  )
}

/** Currency follows the chosen reward, falling back to the account's currency. */
function deriveCurrency(
  values: Record<string, string>,
  options: RewardsOptions | null,
): string {
  if (!options) return ''
  const item = options.catalog.find((i) => i.utid === values.default_utid)
  if (item?.currency) return item.currency
  const account = options.accounts.find(
    (a) => a.account_identifier === values.account_identifier,
  )
  return account?.currency_code ?? ''
}

function extractMessage(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = (err as any)?.response?.data
    if (data && typeof data === 'object') {
      if (typeof data.message === 'string') return data.message
      if (typeof data.detail === 'string') return data.detail
    }
  }
  return 'Something went wrong. Try again.'
}
